import { DateTime } from 'luxon'
import { ReactNode } from 'react'
import { SupportedMapLayers } from '@stores/enums'
import { IGeoJSON, SimplePointType, SimplePolygonType } from '@stores/GeoJSON'
import { ISoilSample } from '@stores/SoilSample'
import { IVI } from '@stores/VI'
import { CropProgressResponse, CropResponse } from '@services/responseTypes'

export const DEFAULT_CRS = 'EPSG:3857'
export type CRSType = 'EPSG:3857' | 'EPSG:4326'
export type LFMapMode = 'view' | 'interact'
export type LFDrawMode = 'none' | 'point' | 'polygon'

export interface LFMapOptions {
  withSocket?: boolean
  withSearch?: boolean
  isArchived?: boolean
  withWeather?: boolean
  isEditable?: boolean
  isDrawable?: boolean
  withVI?: boolean
  withSelectedFeature?: boolean
  withSoil?: boolean
  withControls?: boolean
  withFieldBoundary?: boolean
  withCropzoneEditing?: boolean
  withHashSyncControl?: boolean
  withMapViewPersistence?: boolean
  autoZoomToClusters?: boolean
}

export type LFMapDefaultLocation = {
  center: number[] // lon,lat
  zoom: number
}

export type LFMapProps = {
  mode?: LFMapMode
  defaultLocation?: LFMapDefaultLocation
  mapLayerType?: SupportedMapLayers
  selectedFeature?: LFMapGeoJSON | null
  field?: IGeoJSON
  options?: LFMapOptions
  height?: string
  vis?: IVI[]
  mapID?: string
  mapLayerPills?: SupportedMapLayerPills[]
  drawMode?: LFDrawMode
  children?: ReactNode
}

export type GeoJSONProperties = {
  key: string
  centroid?: number[]
  name: string
  farm?: { id: string | null; name: string | null; phone: string | null }
  plantingDate?: string | null
  area?: number
  crop?: CropResponse
  cropzoneID?: string
  scaleTargetId: string
  crs: CRSType
  cursor: CSSStyleDeclaration['cursor']
  nextCropzoneId?: string
  archivedAt?: DateTime | null
  url?: string
  soilSamples?: ISoilSample[]
  groupedSoilSamples?: ISoilSample[]
  polygonArea?: number
  latestVi?: IVI[]
  fieldID?: string
  amountOfSeeds?: number
  amountOfSeeds_unit?: string
  yieldColor?: string
  cropProgressColor?: string
  cropProgress?: CropProgressResponse | null
  gdd?: {
    current_gdd: number
    target_gdd: number
    percentage: string
    is_matured: boolean
  } | null
  actual?: {
    harvestingDate: string | null
    harvest: number | null
    yield: number | null
  }
  predicted?: {
    maturityDate: string | null
  }
  createdAt?: string
}

export interface LFMapGeoJSON {
  geometry: SimplePolygonType | SimplePointType
  properties: GeoJSONProperties
}

export type SupportedSoilTextures =
  | 'clay'
  | 'sandy_clay'
  | 'sandy_clay_loam'
  | 'sandy_loam'
  | 'loamy_sand'
  | 'sandy'
  | 'loam'
  | 'clay_loam'
  | 'silty_clay'
  | 'silty_clay_loam'
  | 'silty_loam'
  | 'silt'
  | 'no_data'
  | 'other'

export interface SupportedMapLayerPills {
  label: string
  mapLayerType: SupportedMapLayers
}
